import React, { useState } from 'react';
import { Check, X, Edit3, Loader2, AlertTriangle, Play, Square, Pause, Settings } from 'lucide-react';
import Badge from '@/components/UI/Badge';

const ACTION_CONFIG = {
  adjust_params: { label: 'Adjust Parameters', icon: Settings, color: 'border-blue-500/30 bg-blue-500/5' },
  start_strategy: { label: 'Start Strategy', icon: Play, color: 'border-emerald-500/30 bg-emerald-500/5' },
  stop_strategy: { label: 'Stop Strategy', icon: Square, color: 'border-red-500/30 bg-red-500/5' },
  pause_strategy: { label: 'Pause Strategy', icon: Pause, color: 'border-amber-500/30 bg-amber-500/5' },
};

function ParamDiff({ currentParams, proposedChanges, editedChanges, onEdit, editing }) {
  const changedKeys = Object.keys(proposedChanges);

  return (
    <div className="space-y-1.5 mt-2">
      {changedKeys.map((key) => {
        const oldVal = currentParams[key];
        const newVal = editing ? (editedChanges[key] ?? proposedChanges[key]) : proposedChanges[key];
        const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);
        return (
          <div key={key} className="flex items-center justify-between text-xs gap-2">
            <span className="text-muted-foreground font-mono truncate min-w-0">{key}</span>
            <div className="flex items-center gap-2 shrink-0">
              {oldVal != null && (
                <span className="text-red-400/60 line-through">{JSON.stringify(oldVal)}</span>
              )}
              <span className="text-muted-foreground">→</span>
              {editing ? (
                <input
                  type="text"
                  value={String(editedChanges[key] ?? newVal)}
                  onChange={(e) => {
                    let val = e.target.value;
                    // Try to parse as number
                    if (!isNaN(val) && val.trim() !== '') val = Number(val);
                    onEdit({ ...editedChanges, [key]: val });
                  }}
                  className="w-24 px-1.5 py-0.5 bg-card border border-white/10 rounded text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                />
              ) : (
                <span className={changed ? 'text-emerald-400 font-semibold' : 'text-foreground'}>
                  {JSON.stringify(newVal)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ActionProposalCard({ action, onAccept, onDecline, onStatusChange }) {
  const [status, setStatus] = useState(action.status || 'pending');
  const [editing, setEditing] = useState(false);
  const [editedChanges, setEditedChanges] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cfg = ACTION_CONFIG[action.actionType] || ACTION_CONFIG.adjust_params;
  const Icon = cfg.icon;
  const isPending = status === 'pending';

  const handleAccept = async () => {
    setLoading(true);
    setError(null);
    try {
      const overrides = editing && Object.keys(editedChanges).length > 0
        ? editedChanges
        : null;
      await onAccept(action, overrides);
      setStatus('executed');
      onStatusChange?.(action.actionId, 'executed');
    } catch (err) {
      setError(err.message);
      setStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setLoading(true);
    try {
      await onDecline(action);
      setStatus('rejected');
      onStatusChange?.(action.actionId, 'rejected');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`rounded-lg border p-3 mt-2 ${isPending ? cfg.color : status === 'executed' ? 'border-emerald-500/20 bg-emerald-500/5' : status === 'rejected' ? 'border-white/5 bg-white/[0.02] opacity-60' : 'border-red-500/20 bg-red-500/5'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-foreground" />
          <span className="text-xs font-semibold text-foreground">{cfg.label}</span>
        </div>
        <Badge variant={status === 'pending' ? 'warning' : status === 'executed' ? 'success' : status === 'rejected' ? 'neutral' : 'danger'} className="text-[9px]">
          {status}
        </Badge>
      </div>

      {/* Strategy info */}
      <p className="text-xs text-muted-foreground">
        {action.strategyName || action.strategyId?.slice(0, 8)}
        {action.strategyStatus && <span className="ml-1.5 text-[10px]">({action.strategyStatus})</span>}
      </p>

      {/* Parameter diff for adjust_params */}
      {action.actionType === 'adjust_params' && action.proposedChanges && (
        <ParamDiff
          currentParams={action.currentParams || {}}
          proposedChanges={action.proposedChanges}
          editedChanges={editedChanges}
          onEdit={setEditedChanges}
          editing={editing && isPending}
        />
      )}

      {error && (
        <div className="flex items-center gap-1.5 mt-2 text-xs text-red-400">
          <AlertTriangle className="w-3 h-3" />
          {error}
        </div>
      )}

      {/* Actions */}
      {isPending && (
        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-white/5">
          {action.actionType === 'adjust_params' && (
            <button
              onClick={() => { setEditing(!editing); if (!editing) setEditedChanges({ ...action.proposedChanges }); }}
              className="flex items-center gap-1 px-2.5 py-1 text-[10px] rounded-md border border-white/10 text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors"
            >
              <Edit3 className="w-3 h-3" />
              {editing ? 'Cancel Edit' : 'Edit'}
            </button>
          )}
          <div className="flex gap-1.5 ml-auto">
            <button
              onClick={handleDecline}
              disabled={loading}
              className="flex items-center gap-1 px-2.5 py-1 text-[10px] rounded-md bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors disabled:opacity-50"
            >
              <X className="w-3 h-3" />
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={loading}
              className="flex items-center gap-1 px-2.5 py-1 text-[10px] rounded-md bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Accept
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
